import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Divider from "@material-ui/core/Divider";
import DirectionsBoatIcon from "@material-ui/icons/DirectionsBoat";
import BuildIcon from "@material-ui/icons/Build";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import TableChartIcon from "@material-ui/icons/TableChart";

import commons from "../common/commons";
import utils from "../../universal/utils";
import ConvertToOnDemandPanel from "./ConvertToOnDemandPanel";
import EditOnDemandItemsDialog from "./EditOnDemandItemsDialog";
import PreorderSheetSyncDialog from "./PreorderSheetSyncDialog";

const basePath = utils.getEdgeBasePath();

function formatDmY(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

// Shared status catalog (mirrors edge/constants/onDemandStatuses.js)
const ON_DEMAND_STATUSES = [
  { key: "received", title: "ההזמנה התקבלה", order: 1 },
  { key: "paid", title: "התשלום אושר", order: 2 },
  { key: "left_supplier_to_logistics_center", title: "הצמיגים יצאו מהספק", order: 3 },
  { key: "arrived_logistics_center", title: "הצמיגים הגיעו למרכז הלוגיסטי", order: 4 },
  { key: "in_eu_customs", title: "הצמיגים במכס באירופה", order: 5 },
  { key: "container_enroute_to_israel", title: "המכולה בדרך לישראל", order: 6 },
  { key: "arrived_israel_pending_customs", title: "המכולה הגיעה לישראל", order: 7 },
  { key: "container_released", title: "המכולה שוחררה", order: 8 },
  { key: "tires_shipped", title: "הצמיגים נשלחו", order: 9 },
  { key: "order_complete", title: "ההזמנה הושלמה", order: 10, terminal: true },
  { key: "cancelled", title: "ההזמנה בוטלה", order: 11, terminal: true },
];

const STATUS_MAP = Object.fromEntries(ON_DEMAND_STATUSES.map((s) => [s.key, s]));

function statusChipColor(key) {
  if (key === "order_complete") return "#16a34a";
  if (key === "cancelled") return "#dc2626";
  if (key === "tires_shipped") return "#7c3aed";
  if (["received", "paid"].includes(key)) return "#2563eb";
  return "#d97706";
}

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: theme.spacing(2),
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    padding: "12px 16px",
    background: "#eff6ff",
    borderRadius: 8,
    border: "1px solid #bfdbfe",
    marginBottom: theme.spacing(2),
  },
  orderCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    border: "1px solid #e5e7eb",
    borderRadius: 8,
  },
  orderHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  orderMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 18px",
    color: "#4b5563",
    marginBottom: 10,
  },
  orderMetaText: {
    fontSize: 16,
    lineHeight: 1.6,
  },
  itemsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 10,
  },
  itemChip: {
    fontSize: 15,
    height: 34,
    fontWeight: 600,
  },
  itemTraceBlock: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
    minWidth: 0,
    flex: "1 1 auto",
  },
  itemImage: {
    width: 78,
    height: 78,
    flexShrink: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    background: "#f9fafb",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },
  itemTextBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  copyableText: {
    userSelect: "text",
    direction: "ltr",
    textAlign: "left",
    fontFamily: "monospace",
    fontSize: 14,
    lineHeight: 1.45,
    color: "#374151",
  },
  updateHistory: {
    marginTop: 10,
    padding: "12px 16px",
    background: "#f9fafb",
    borderRadius: 6,
  },
  updateEntry: {
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 6,
    marginBottom: 6,
    "&:last-child": { borderBottom: "none", marginBottom: 0 },
  },
  updateEntryRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: "2px 10px",
  },
  updateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  updateMessage: {
    fontSize: 15,
    color: "#d97706",
  },
  updateTimestamp: {
    fontSize: 14,
    color: "#9ca3af",
  },
  statusChip: {
    color: "white",
    fontWeight: 600,
    fontSize: 14,
    height: 30,
  },
  actionBtn: {
    minWidth: 120,
  },
  divider: {
    margin: "16px 0",
  },
  emptyState: {
    textAlign: "center",
    padding: 40,
    color: "#6b7280",
  },
});

class OnDemandOrdersManager extends Component {
  state = {
    orders: [],
    loading: false,
    error: null,
    pendingOnly: true,
    filterStatus: "",
    selectedIds: new Set(),
    bulkStatus: "",
    bulkCustomMessage: "",
    bulkSendEmail: false,
    bulkLoading: false,
    bulkResult: null,
    // Per-order update state
    perOrderState: {},
    // Supplier backfill
    backfillLoading: false,
    backfillResult: null,
    depositBackfillLoading: false,
    depositBackfillResult: null,
    editingOrder: null,
    sheetSyncConfigured: false,
    sheetSyncTarget: null,
    sheetSyncOpen: false,
  };

  componentDidMount() {
    this.loadOrders();
    this.loadSheetSyncConfig();
  }

  loadSheetSyncConfig = () => {
    fetch(`${basePath}/api/v1/admin/google-sheets-sync`, {
      headers: utils.getAdminAuthHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data || !data.ok || !data.config) return;
        const account = data.config.serviceAccount || {};
        this.setState({
          sheetSyncConfigured: Boolean(account.configured && account.decryptable && data.config.spreadsheetId),
          sheetSyncTarget: { sheetUrl: data.config.sheetUrl || "" },
        });
      })
      .catch(() => {});
  };

  selectUnsyncedPending = () => {
    const ids = (this.state.orders || [])
      .filter((order) => !order.onDemand?.sheetSync && ["received", "paid"].includes(order.onDemand?.status))
      .map((order) => order.orderId);
    this.setState({ selectedIds: new Set(ids) });
  };

  loadOrders = () => {
    const { pendingOnly, filterStatus } = this.state;
    this.setState({ loading: true, error: null, selectedIds: new Set() });

    const params = new URLSearchParams();
    if (!pendingOnly) params.set("pendingOnly", "false");
    if (filterStatus) params.set("status", filterStatus);

    fetch(`${basePath}/api/v1/on-demand-orders?${params}`, {
      headers: utils.getAdminAuthHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "שגיאת שרת");
        this.setState({ orders: data.orders || [], loading: false });
      })
      .catch((err) => {
        this.setState({ loading: false, error: err.message });
      });
  };

  toggleSelected = (orderId) => {
    this.setState((prev) => {
      const next = new Set(prev.selectedIds);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return { selectedIds: next };
    });
  };

  selectAll = () => {
    const { orders } = this.state;
    this.setState({ selectedIds: new Set(orders.map((o) => o.orderId)) });
  };

  deselectAll = () => {
    this.setState({ selectedIds: new Set() });
  };

  handleBackfillSupplier = () => {
    this.setState({ backfillLoading: true, backfillResult: null });
    fetch(`${basePath}/api/v1/on-demand-orders/backfill-supplier`, {
      method: "POST",
      headers: utils.getAdminAuthHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        this.setState({ backfillLoading: false, backfillResult: data });
        if (data.ok && data.updated > 0) this.loadOrders();
      })
      .catch((err) => {
        this.setState({ backfillLoading: false, backfillResult: { ok: false, error: err.message } });
      });
  };

  handleBackfillDeposits = () => {
    this.setState({ depositBackfillLoading: true, depositBackfillResult: null });
    fetch(`${basePath}/api/v1/on-demand-orders/backfill-deposits`, {
      method: "POST",
      headers: utils.getAdminAuthHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        this.setState({ depositBackfillLoading: false, depositBackfillResult: data });
        if (data.ok && data.updated > 0) this.loadOrders();
      })
      .catch((err) => {
        this.setState({
          depositBackfillLoading: false,
          depositBackfillResult: { ok: false, error: err.message },
        });
      });
  };

  handleBulkUpdate = () => {
    const { selectedIds, bulkStatus, bulkCustomMessage, bulkSendEmail } = this.state;
    if (!bulkStatus || !selectedIds.size) return;

    this.setState({ bulkLoading: true, bulkResult: null });

    fetch(`${basePath}/api/v1/on-demand-orders/bulk-update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...utils.getAdminAuthHeaders(),
      },
      body: JSON.stringify({
        orderIds: Array.from(selectedIds),
        newStatus: bulkStatus,
        customMessage: bulkCustomMessage,
        sendEmail: bulkSendEmail,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        this.setState({ bulkLoading: false, bulkResult: data });
        if (data.ok) {
          this.loadOrders();
        }
      })
      .catch((err) => {
        this.setState({ bulkLoading: false, bulkResult: { ok: false, error: err.message } });
      });
  };

  setPerOrderField = (orderId, field, value) => {
    this.setState((prev) => ({
      perOrderState: {
        ...prev.perOrderState,
        [orderId]: { ...(prev.perOrderState[orderId] || {}), [field]: value },
      },
    }));
  };

  handleSaveOrderItems = (replacements) => {
    const { editingOrder } = this.state;
    if (!editingOrder) return Promise.reject(new Error("אין הזמנה לעריכה"));

    return fetch(`${basePath}/api/v1/on-demand-orders/${editingOrder.orderId}/items`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...utils.getAdminAuthHeaders(),
      },
      body: JSON.stringify({ replacements }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "שגיאה בעדכון המוצרים");
        this.setState({ editingOrder: null });
        this.loadOrders();
      });
  };

  handlePerOrderUpdate = (orderId) => {
    const { perOrderState } = this.state;
    const state = perOrderState[orderId] || {};
    if (!state.status) return;

    this.setPerOrderField(orderId, "loading", true);
    this.setPerOrderField(orderId, "result", null);

    fetch(`${basePath}/api/v1/on-demand-orders/${orderId}/status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...utils.getAdminAuthHeaders(),
      },
      body: JSON.stringify({
        newStatus: state.status,
        customMessage: state.customMessage || "",
        sendEmail: state.sendEmail || false,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        this.setPerOrderField(orderId, "loading", false);
        this.setPerOrderField(orderId, "result", data);
        if (data.ok) this.loadOrders();
      })
      .catch((err) => {
        this.setPerOrderField(orderId, "loading", false);
        this.setPerOrderField(orderId, "result", { ok: false, error: err.message });
      });
  };

  renderStatusChip(statusKey) {
    const entry = STATUS_MAP[statusKey];
    return (
      <Chip
        label={entry ? entry.title : statusKey}
        size="small"
        className={this.props.classes.statusChip}
        style={{ backgroundColor: statusChipColor(statusKey) }}
      />
    );
  }

  renderOrderCard(order) {
    const { classes } = this.props;
    const { selectedIds, perOrderState } = this.state;
    const ps = perOrderState[order.orderId] || {};
    const isSelected = selectedIds.has(order.orderId);

    const createdDate = order.createdAt
      ? new Date(order.createdAt).toLocaleDateString("he-IL")
      : "-";

    return (
      <Paper key={order.orderId} className={classes.orderCard} elevation={1}>
        <div className={classes.orderHeader}>
          <Checkbox
            checked={isSelected}
            onChange={() => this.toggleSelected(order.orderId)}
            color="primary"
          />
          <Typography variant="h6" style={{ minWidth: 100, fontSize: 26, fontWeight: 700 }}>
            #{order.orderId}
          </Typography>
          {this.renderStatusChip(order.onDemand?.status)}
          {order.onDemand?.sheetSync && (
            <Tooltip title={`${order.onDemand.sheetSync.spreadsheetTitle || ""} / ${order.onDemand.sheetSync.sheetTitle || ""} · שורות ${order.onDemand.sheetSync.rowsFrom}–${order.onDemand.sheetSync.rowsTo}`}>
              <span>
                <Chip
                  icon={<TableChartIcon />}
                  label={`סונכרן לגיליון · ${formatDmY(order.onDemand.sheetSync.syncedAt)}`}
                  size="small"
                  variant="outlined"
                  style={{ color: "#16a34a", borderColor: "#16a34a" }}
                />
              </span>
            </Tooltip>
          )}
          {order.onDemand?.fulfilled && (
            <Tooltip title="הסתיים">
              <CheckCircleIcon style={{ color: "#16a34a", fontSize: 20 }} />
            </Tooltip>
          )}
          <Typography variant="body1" style={{ marginRight: "auto", color: "#6b7280", fontSize: 16 }}>
            {createdDate}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => this.setState({ editingOrder: order })}
          >
            עדכן מוצרים
          </Button>
        </div>

        <div className={classes.orderMeta}>
          <Typography component="span" className={classes.orderMetaText}>
            לקוח: {order.customer?.name || "-"}
          </Typography>
          {order.customer?.phone && (
            <Typography component="span" className={classes.orderMetaText}>
              טל: {order.customer.phone}
            </Typography>
          )}
          {order.customer?.email && (
            <Typography component="span" className={classes.orderMetaText}>
              אימייל: {order.customer.email}
            </Typography>
          )}
        </div>

        {/* Items */}
        <div className={classes.itemsRow}>
          {(order.onDemand?.items || []).map((item, i) => {
            // Prefer data captured at checkout time; fall back to cartByEan from older orders.
            const cart = order.cartByEan?.[item.ean] || {};
            const supplier = item.supplierAtCheckout || cart.supplier || null;
            const costEUR = item.supplierCostEURAtCheckout ?? cart.supplierCostEUR ?? null;
            const retailILS = item.retailPriceILSAtCheckout ?? cart.retailPriceILS ?? null;
            const syntheticSetEans = typeof item.ean === "string" && item.ean.startsWith("GSET-")
              ? item.ean.replace(/^GSET-/, "").split("-")
              : [];
            const components = item.componentsAtCheckout || cart.componentsAtCheckout || (
              syntheticSetEans.length === 2
                ? [
                    { position: "front", ean: syntheticSetEans[0] },
                    { position: "rear", ean: syntheticSetEans[1] },
                  ]
                : []
            );

            const itemBrand = item.brand || cart.brand || "";
            const itemName = item.displayName || cart.displayName || item.ean;
            const itemTitle = [itemBrand, itemName].filter(Boolean).join(" ");
            const chipLabel = `${itemTitle} ${item.displaySize || ""} ×${item.quantity}`.trim();
            const priceText = costEUR != null
              ? `€${Number(costEUR).toFixed(2)} ➜ ₪${retailILS != null ? Number(retailILS).toFixed(0) : "?"}`
              : (retailILS != null ? `₪${Number(retailILS).toFixed(0)}` : "");
            const hasNoSupplier = !supplier && !costEUR;
            const fallbackImageEan =
              components.find((component) => component.position === "rear")?.ean ||
              components.find((component) => component.ean)?.ean ||
              item.ean;
            const isGeorgiaItem = item.source === "georgia" || Boolean(item.supplierAtCheckout || cart.supplier);
            const imageUrl = commons.getProductPrimaryImage(
              item.imageUrl || cart.imageUrl || false,
              fallbackImageEan,
              isGeorgiaItem
            ).url;

            return (
              <Tooltip
                key={i}
                title={
                  <span>
                    <b>EAN:</b> {item.ean}
                    {supplier && <><br /><b>ספק:</b> {supplier}</>}
                    {costEUR != null && <><br /><b>עלות ספק:</b> €{Number(costEUR).toFixed(2)}</>}
                    {retailILS != null && <><br /><b>מחיר מכירה:</b> ₪{Number(retailILS).toFixed(0)}</>}
                    {hasNoSupplier && <><br /><i style={{ color: "#fbbf24" }}>פרטי ספק לא נשמרו</i></>}
                  </span>
                }
                arrow
              >
                <div className={classes.itemTraceBlock}>
                  {imageUrl && (
                    <div
                      className={classes.itemImage}
                      style={{ backgroundImage: `url(${imageUrl})` }}
                    />
                  )}
                  <div className={classes.itemTextBlock}>
                    <Chip
                      label={chipLabel}
                      size="small"
                      variant="outlined"
                      className={classes.itemChip}
                      style={hasNoSupplier ? { borderColor: "#f59e0b", color: "#92400e" } : {}}
                      icon={<DirectionsBoatIcon style={{ fontSize: 16 }} />}
                    />
                    {(priceText || supplier) && (
                      <div className={classes.copyableText} style={{ fontSize: 15, fontFamily: "inherit", color: "#374151" }}>
                        {supplier && <span style={{ fontWeight: 600 }}>{supplier}</span>}
                        {supplier && priceText && <span style={{ color: "#9ca3af" }}> · </span>}
                        {priceText && <span>{priceText}</span>}
                        {hasNoSupplier && <span style={{ color: "#f59e0b", fontStyle: "italic" }}>פרטי ספק לא נשמרו</span>}
                      </div>
                    )}
                    {components.length > 0 ? (
                      components.map((component) => (
                        <div key={`${component.position}-${component.ean}`} className={classes.copyableText}>
                          {component.position}: {component.ean || "-"}
                          {component.supplierAtCheckout ? ` | supplier: ${component.supplierAtCheckout}` : ""}
                          {component.supplierCostEURAtCheckout != null ? ` | EUR: ${Number(component.supplierCostEURAtCheckout).toFixed(2)}` : ""}
                        </div>
                      ))
                    ) : (
                      <div className={classes.copyableText}>EAN: {item.ean}</div>
                    )}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>

        {/* Update history */}
        {(order.onDemand?.updates || []).length > 0 && (
          <div className={classes.updateHistory}>
            <Typography variant="body2" style={{ fontWeight: 700, display: "block", marginBottom: 8, fontSize: 16 }}>
              היסטוריית עדכונים:
            </Typography>
            {[...(order.onDemand.updates || [])].reverse().slice(0, 5).map((u, i) => (
              <div key={i} className={classes.updateEntry}>
                <div className={classes.updateEntryRow}>
                  <Typography component="span" className={classes.updateTitle}>
                    {u.title || u.status}
                  </Typography>
                  {u.customMessage && (
                    <Typography component="span" className={classes.updateMessage}>
                      {u.customMessage}
                    </Typography>
                  )}
                  <Typography component="span" className={classes.updateTimestamp}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleString("he-IL") : ""}
                    {u.emailed ? " ✉" : ""}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Per-order update controls */}
        {!order.onDemand?.fulfilled && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <FormControl style={{ minWidth: 220 }}>
              <InputLabel>עדכן סטטוס</InputLabel>
              <Select
                value={ps.status || ""}
                onChange={(e) => this.setPerOrderField(order.orderId, "status", e.target.value)}
              >
                {ON_DEMAND_STATUSES.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="הודעה אישית (אופציונלי)"
              value={ps.customMessage || ""}
              onChange={(e) => this.setPerOrderField(order.orderId, "customMessage", e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={ps.sendEmail || false}
                  onChange={(e) => this.setPerOrderField(order.orderId, "sendEmail", e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="שלח מייל"
            />

            <Button
              variant="contained"
              color="primary"
              disabled={!ps.status || ps.loading}
              onClick={() => this.handlePerOrderUpdate(order.orderId)}
              className={classes.actionBtn}
            >
              {ps.loading ? <CircularProgress size={20} /> : "עדכן"}
            </Button>

            {ps.result && (
              <Typography
                variant="caption"
                style={{ color: ps.result.ok ? "#16a34a" : "#dc2626" }}
              >
                {ps.result.ok ? "עודכן בהצלחה" : ps.result.error}
              </Typography>
            )}
          </div>
        )}
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    const {
      orders,
      loading,
      error,
      pendingOnly,
      filterStatus,
      selectedIds,
      bulkStatus,
      bulkCustomMessage,
      bulkSendEmail,
      bulkLoading,
      bulkResult,
      backfillLoading,
      backfillResult,
      depositBackfillLoading,
      depositBackfillResult,
    } = this.state;

    return (
      <div className={classes.root} style={{ direction: "rtl" }}>
        <ConvertToOnDemandPanel onConverted={this.loadOrders} />
        <EditOnDemandItemsDialog
          open={Boolean(this.state.editingOrder)}
          order={this.state.editingOrder}
          onClose={() => this.setState({ editingOrder: null })}
          onSave={this.handleSaveOrderItems}
        />
        <PreorderSheetSyncDialog
          open={this.state.sheetSyncOpen}
          orderIds={Array.from(selectedIds)}
          onClose={() => this.setState({ sheetSyncOpen: false })}
          onSynced={() => {
            this.setState({ sheetSyncOpen: false });
            this.loadOrders();
          }}
        />
        {/* Toolbar */}
        <div className={classes.toolbar}>
          <FormControlLabel
            control={
              <Switch
                checked={pendingOnly}
                onChange={(e) => this.setState({ pendingOnly: e.target.checked }, this.loadOrders)}
                color="primary"
              />
            }
            label="הצג ממתינות בלבד"
          />

          <FormControl style={{ minWidth: 200 }}>
            <InputLabel>סנן לפי סטטוס</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => this.setState({ filterStatus: e.target.value }, this.loadOrders)}
            >
              <MenuItem value="">כל הסטטוסים</MenuItem>
              {ON_DEMAND_STATUSES.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={this.loadOrders}
          >
            רענן
          </Button>

          <Tooltip title="נסה לשחזר פרטי ספק להזמנות שחסרים להן נתונים (מ-georgiaMeta של העגלה ו/או מה-API של Georgia עבור הזמנות עד 5 ימים)">
            <span>
              <Button
                variant="outlined"
                startIcon={backfillLoading ? <CircularProgress size={16} /> : <BuildIcon />}
                disabled={backfillLoading}
                onClick={this.handleBackfillSupplier}
                style={{ borderColor: "#d97706", color: "#92400e" }}
              >
                שחזר ספקים
              </Button>
            </span>
          </Tooltip>
          {backfillResult && (
            <Typography
              variant="caption"
              style={{ color: backfillResult.ok ? "#16a34a" : "#dc2626", maxWidth: 240 }}
            >
              {backfillResult.ok
                ? `עודכנו ${backfillResult.updated} הזמנות (עובדו ${backfillResult.processed})`
                : backfillResult.error}
            </Typography>
          )}

          <Tooltip title="שחזר הזמנות מקדמה ששולמו בחודש האחרון ונוצרו לפני שנשמרו כפריטי מלאי אירופה">
            <span>
              <Button
                variant="outlined"
                startIcon={depositBackfillLoading ? <CircularProgress size={16} /> : <BuildIcon />}
                disabled={depositBackfillLoading}
                onClick={this.handleBackfillDeposits}
                style={{ borderColor: "#2563eb", color: "#1d4ed8" }}
              >
                שחזר מקדמות חודש
              </Button>
            </span>
          </Tooltip>
          {depositBackfillResult && (
            <Typography
              variant="caption"
              style={{ color: depositBackfillResult.ok ? "#16a34a" : "#dc2626", maxWidth: 260 }}
            >
              {depositBackfillResult.ok
                ? `עודכנו ${depositBackfillResult.updated} הזמנות מקדמה (עובדו ${depositBackfillResult.processed})`
                : depositBackfillResult.error}
            </Typography>
          )}

          {orders.length > 0 && (
            <>
              <Button size="small" onClick={this.selectAll}>
                בחר הכל ({orders.length})
              </Button>
              <Button size="small" onClick={this.selectUnsyncedPending}>
                בחר ממתינות שלא סונכרנו לגיליון
              </Button>
              <Button size="small" onClick={this.deselectAll}>
                בטל בחירה
              </Button>
            </>
          )}
        </div>

        {/* Bulk update bar */}
        {selectedIds.size > 0 && (
          <div className={classes.bulkBar}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {selectedIds.size} הזמנות נבחרו
            </Typography>
            {this.state.sheetSyncConfigured ? (
              <Button
                variant="outlined"
                startIcon={<TableChartIcon />}
                onClick={() => this.setState({ sheetSyncOpen: true })}
                style={{ borderColor: "#16a34a", color: "#16a34a" }}
              >
                {`סנכרן לגיליון ההזמנה (${selectedIds.size})`}
              </Button>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Tooltip title='הגדר חיבור לגיליון במסך "חיבור Google Sheets"'>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<TableChartIcon />}
                      disabled
                    >
                      {`סנכרן לגיליון ההזמנה (${selectedIds.size})`}
                    </Button>
                  </span>
                </Tooltip>
                <Link to="/admin/google-sheets-sync">הגדרות</Link>
              </span>
            )}

            <FormControl style={{ minWidth: 220 }}>
              <InputLabel>סטטוס חדש (bulk)</InputLabel>
              <Select
                value={bulkStatus}
                onChange={(e) => this.setState({ bulkStatus: e.target.value })}
              >
                {ON_DEMAND_STATUSES.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="הודעה מותאמת (אופציונלי)"
              value={bulkCustomMessage}
              onChange={(e) => this.setState({ bulkCustomMessage: e.target.value })}
              style={{ flex: 1, minWidth: 180 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={bulkSendEmail}
                  onChange={(e) => this.setState({ bulkSendEmail: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label="שלח מייל ללקוחות"
            />

            <Button
              variant="contained"
              color="primary"
              disabled={!bulkStatus || bulkLoading}
              onClick={this.handleBulkUpdate}
            >
              {bulkLoading ? <CircularProgress size={20} /> : `עדכן ${selectedIds.size} הזמנות`}
            </Button>

            {bulkResult && (
              <Typography
                variant="caption"
                style={{ color: bulkResult.ok ? "#16a34a" : "#dc2626" }}
              >
                {bulkResult.ok
                  ? `עודכנו ${bulkResult.updated} הזמנות${bulkResult.failed ? `, נכשלו ${bulkResult.failed}` : ""}`
                  : bulkResult.error}
              </Typography>
            )}
          </div>
        )}

        <Divider className={classes.divider} />

        {/* Content */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <CircularProgress />
          </div>
        )}

        {error && (
          <Typography color="error" style={{ padding: 16 }}>
            שגיאה: {error}
          </Typography>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className={classes.emptyState}>
            <DirectionsBoatIcon style={{ fontSize: 48, color: "#d1d5db", display: "block", margin: "0 auto 12px" }} />
            <Typography variant="body1">אין הזמנות מראש להצגה</Typography>
          </div>
        )}

        {!loading && orders.map((order) => this.renderOrderCard(order))}
      </div>
    );
  }
}

OnDemandOrdersManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(OnDemandOrdersManager);
